import React, { CSSProperties, FormEvent } from 'react'
import Downshift from 'downshift'
import { Flex, Icon, IconButton, PrettyNumber } from '@ui'
import { InputGroup, Menu, Classes, Spinner } from '@blueprintjs/core'
import { useIntersect } from '@hooks'
import useEmployeeCatalog, {
  EmployeeCatalog,
  EmployeeCatalogFiltersT,
} from './hooks/useEmployeeCatalog'
import { ICatalogEmployee } from '@api'
import EmployeeCard from './EmployeeCard'
import { pluralize } from '@utils'
import FiltersComponent from './FiltersComponent'

interface ToolbarProps {
  catalog: EmployeeCatalog
  style?: CSSProperties
}

interface EmployeeCatalogProps {
  assignmentPlanId?: string
  draggable?: boolean
  filterable?: boolean
  multi?: boolean
  selection?: string[]
  onSelect?: (employee: ICatalogEmployee) => void
  height?: number | string
  alreadyAssigned?: { id: string }[]
  itemDisabled?: (item: { id: string }) => boolean
}

function getFilterCount(filters: EmployeeCatalogFiltersT) {
  let count = 0

  if (filters.cities.length) {
    count++
  }

  count += filters.valueFilter.length
  count += Object.values(filters.keyValueFilters).filter((v) => !!v).length
  count += Object.values(filters.filters).filter((v) => !!v).length

  return count
}

function Toolbar({ catalog }: ToolbarProps) {
  const { isPaging, isLoading, count, filters, clearFilters } = catalog

  const filterCount = getFilterCount(filters)
  let helperText = null
  let clearButton = null

  if (!isLoading || isPaging) {
    if (filterCount) {
      helperText = (
        <span>
          <PrettyNumber value={count} /> filtered {pluralize(count, 'record')}
        </span>
      )

      clearButton = (
        <IconButton
          minimal
          tooltip="Clear filters"
          iconName="times"
          style={{ marginLeft: 4 }}
          onClick={() => {
            clearFilters()
          }}
        />
      )
    } else {
      helperText = (
        <span>
          <PrettyNumber value={count} /> {pluralize(count, 'record')}
        </span>
      )
    }
  }

  return (
    <Flex
      align="center"
      style={{
        height: 30,
        marginLeft: 10,
        marginBottom: -5,
        color: 'var(--grey1)',
        fontSize: 12,
      }}
    >
      {helperText ? helperText : <div className={Classes.SKELETON}>Placeholder text</div>}
      {clearButton}
    </Flex>
  )
}

function Skeleton({ countLines = 6 }: { countLines?: number }) {
  function Placeholder() {
    return (
      <Flex align="center" style={{ padding: '0 12px', height: 40, width: 280 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            marginRight: 10,
          }}
          className={Classes.SKELETON}
        >
          Placeholder text
        </div>
        <div
          style={{
            width: 150,
            height: 16,
          }}
          className={Classes.SKELETON}
        >
          Placeholder text
        </div>
      </Flex>
    )
  }

  return (
    <>
      {Array.from({ length: countLines }, (_, k) => (
        <Placeholder key={k} />
      ))}
    </>
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear?.()
        }}
      />
    )
  return null
}

export default function ({
  assignmentPlanId,
  draggable,
  filterable,
  multi,
  selection,
  onSelect,
  height = '40%',
  itemDisabled,
}: EmployeeCatalogProps) {
  const catalog = useEmployeeCatalog(assignmentPlanId, filterable)
  const filterCount = getFilterCount(catalog.filters)
  const { refItem, refRoot } = useIntersect({
    threshold: [0.75],
    callback: catalog.next,
  })

  return (
    <Downshift
      itemToString={() => ''}
      onSelect={(employee) => {
        onSelect && onSelect(employee)
      }}
    >
      {({ getInputProps, getMenuProps, getItemProps, highlightedIndex }) => (
        <div
          ref={refRoot}
          style={{
            height,
            position: 'relative',
            overflowY: 'auto',
            borderRadius: 4,
            minWidth: 300,
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              padding: 5,
              paddingBottom: 0,
              background: 'white',
              zIndex: 1,
            }}
          >
            <Flex align="center">
              {/* eslint-disable @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <InputGroup
                {...getInputProps({
                  autoFocus: true,
                  className: Classes.FILL,
                  value: catalog.query,
                  onChange: ({ currentTarget: { value } }: FormEvent<HTMLInputElement>) => {
                    catalog.search(value)
                  },
                  placeholder: 'Search...',
                  leftIcon: 'search',
                  rightElement: (
                    <RightElement
                      showWithoutQuery={false}
                      query={catalog.query}
                      onClear={() => catalog.clearFilters()}
                      isQueryResultLoading={catalog.isLoading}
                    />
                  ),
                  onFocus: ({ currentTarget }: FormEvent<HTMLInputElement>) => {
                    currentTarget.select()
                  },
                })}
              />
              {filterable && (
                <FiltersComponent
                  filterCount={filterCount}
                  filters={catalog.filters}
                  applyFilters={catalog.applyFilters}
                />
              )}
            </Flex>
          </div>
          {filterable && <Toolbar catalog={catalog} />}
          <div className="employeeCatalog_wrapper" {...getMenuProps()}>
            <Menu>
              {catalog.items.map((employee, i) => (
                <EmployeeCard
                  {...getItemProps({
                    key: employee.id,
                    item: employee,
                  })}
                  multi={multi}
                  draggable={draggable}
                  employee={employee}
                  query={catalog.query}
                  active={i === highlightedIndex}
                  selected={selection && selection.includes(employee.id)}
                  disabled={itemDisabled?.(employee)}
                  refItem={i === catalog.items.length - 8 ? refItem : undefined}
                />
              ))}
            </Menu>
            {catalog.isLoading && (
              <Skeleton
                countLines={
                  catalog.items.length ? Math.min(catalog.count - catalog.items.length, 25) : 25
                }
              />
            )}
          </div>
        </div>
      )}
    </Downshift>
  )
}
